import * as React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { authService } from '@/services/AuthService'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { resetPasswordInputSchema } from '@lib/input/user.input'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { AuthShell } from '@/components/AuthShell'
import { AccountEmailField } from '@/components/AccountEmailField'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormError } from '@/components/ui/form-error'

/** Public reset page reached from the emailed link (`?token=`). Sets a new
 * password, then points back to sign in. */
export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const [token] = React.useState(() => params.get('token') ?? '')
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState(false)

  // The token sets a password. It is already in state, so it does not stay in
  // the address bar, where history and the next request's Referer would keep it.
  React.useEffect(() => {
    if (window.location.search.length > 0) {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [])

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    if (password !== confirm) {
      setError('Passwords don’t match.')
      return
    }
    const parsed = resetPasswordInputSchema.safeParse({ token, password })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid password.')
      return
    }
    setSubmitting(true)
    try {
      await authService.resetPassword(parsed.data)
      setDone(true)
    } catch (err) {
      setError(isApiError(err) ? err.body.message ?? 'Could not reset your password.' : toErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-display">Set a new password</CardTitle>
          <CardDescription>{done ? 'All set.' : 'Choose a new password for your account.'}</CardDescription>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted">This reset link is invalid. Request a fresh one to try again.</p>
              <Link to="/forgot-password" className="text-sm underline">
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div className="space-y-4">
              <p className="text-sm text-ink-muted">Your password has been updated.</p>
              <Link to="/login" className="text-sm underline">
                Sign in
              </Link>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <AccountEmailField token={token} />
              <Field label="New password" htmlFor="password">
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Field label="Confirm password" htmlFor="confirm">
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </Field>
              {error ? <FormError>{error}</FormError> : null}
              <Button pending={submitting} type="submit" className="w-full">
                {submitting ? 'Saving…' : 'Reset password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  )
}
